import { get, cloneDeep } from "lodash";
import { createSelector } from "reselect";
import {
  RESOURCE_URI_CHUNKS,
  ALL_RESOURCE_KEYS
} from "../../../constants/entity.js";
import selectorUtils from "../selectorUtils.js";
import operationSelector from "../operations/operationSelector.js";
import { MARKING_STATES } from "../../../utils/marking.js";

// -- QUERIES -- //

const projectsDataIndexQuery = state => get(state, "projects.dataIndex", null);
const projectsDataMapQuery = state => get(state, "projects.dataMap", null);
const chargeDataMapQuery = state => get(state, `charge.dataMap`, null);
const usageDataMapQuery = state => get(state, `usage.dataMap`, null);
const entitiesDataMapQuery = state =>
  get(state, "ephemeralEntities.dataMap", null);

const workstationsQuery = state => get(state, "workstations.dataMap", null);
const displayGatewaysQuery = state =>
  get(state, "displaygateways.dataMap", null);
const containersQuery = state => get(state, "containers.dataMap", null);
const databasesQuery = state => get(state, "databases.dataMap", null);
const nodesQuery = state => get(state, "nodes.dataMap", null);
const virtualPrivateNetworkGatewaysQuery = state =>
  get(state, "virtualprivatenetworkgateways.dataMap", null);
const snowflakesQuery = state => get(state, "snowflakes.dataMap", null);

const connectionsQuery = state => get(state, "connections.dataMap", null);

const chargeQuery = (state, props) => {
  if (!props) return null;
  const id = props.id
    ? props.id
    : selectorUtils.getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.PROJECTS);
  return get(state, `charge.dataMap.${id}`, null);
};

const usageQuery = (state, props) => {
  if (!props) return null;
  const id = props.id
    ? props.id
    : selectorUtils.getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.PROJECTS);
  return get(state, `usage.dataMap.${id}`, null);
};

const projectQuery = (state, props) => {
  if (!props) return null;

  const id = props.id
    ? props.id
    : selectorUtils.getAtIdFromMatchProps(props, RESOURCE_URI_CHUNKS.PROJECTS);
  const projectData = get(state, `projects.dataMap.${id}`, null);

  // create duplicate of selected project obj
  // stringify duplication method will work as all data is in JSON-LD format
  if (projectData) {
    return JSON.parse(JSON.stringify(projectData));
  } else {
    return null;
  }
};

function buildEntityMap(entities, ids) {
  let result = ids;

  if (entities && ids && ids.length > 0) {
    result = ids.map(item => {
      return { ...item, ...entities[item["@id"]] };
    });
  }

  return result;
}

function buildActiveEntityValue(entities) {
  let result = 0;

  if (entities && entities.length > 0) {
    result = entities.reduce((acc, item) => {
      return item.marking === MARKING_STATES.STARTED ||
        item.marking === MARKING_STATES.CONVERGED
        ? acc + 1
        : acc;
    }, 0);
  }

  return result;
}

// -- SELECTORS -- //

const selectProject = () => {
  return createSelector(
    [
      projectQuery,
      connectionsQuery,
      chargeQuery,
      usageQuery,
      operationSelector.permissionsQuery,
      entitiesDataMapQuery,
      // currently order here has to be the same as ALL_RESOURCE_KEYS - ew. super brittle.
      workstationsQuery,
      displayGatewaysQuery,
      containersQuery,
      databasesQuery,
      nodesQuery,
      virtualPrivateNetworkGatewaysQuery,
      snowflakesQuery
    ],
    (
      baseProject,
      connections,
      charge,
      usage,
      permissions,
      entities,
      ...resources
    ) => {
      // bail early if we don't have a project
      if (!baseProject) return null;

      let project = { ...baseProject, currentlyActive: {} };

      // add full resource data into returned project object
      // lack of a property on the original project object indicates a lack of list/view permissions for that data
      // we need to call build entity map so that any resource pusher updates are applied to the project data originally returned
      ALL_RESOURCE_KEYS.forEach((key, index) => {
        if (project.hasOwnProperty(key)) {
          project[key] = projectSelector.buildEntityMap(
            resources[index],
            project[key]
          );
          project.currentlyActive[key] = projectSelector.buildActiveEntityValue(
            project[key]
          );
        }
      });

      if (project.hasOwnProperty("connections")) {
        project.connections = projectSelector.buildEntityMap(
          connections,
          project.connections
        );
      }

      if (project.hasOwnProperty("charge")) {
        project.charge = charge;
      }

      if (project.hasOwnProperty("usage")) {
        project.usage = usage;
      }

      if (project.hasOwnProperty("ownedBy")) {
        project.ownedBy = entities[project.ownedBy];
      }

      if (project.hasOwnProperty("createdBy")) {
        project.createdBy = entities[project.createdBy];
      }

      return operationSelector.stampOperationPermissions(
        cloneDeep(project),
        permissions
      );
    }
  );
};

const selectSlimProject = createSelector(
  [projectQuery, operationSelector.permissionsQuery],
  (baseProject, permissions) => {
    // bail early if we don't have a project
    if (!baseProject) return null;

    return operationSelector.stampOperationPermissions(
      cloneDeep(baseProject),
      permissions
    );
  }
);

const selectProjectsDataMap = createSelector(
  [
    projectsDataIndexQuery,
    projectsDataMapQuery,
    chargeDataMapQuery,
    usageDataMapQuery,
    operationSelector.permissionsQuery
  ],
  (
    projectsDataIndex,
    projectsDataMap,
    chargeDataMap,
    usageDataMap,
    permissions
  ) => {
    if (!projectsDataMap || !projectsDataIndex) return null;

    const projects = { ...projectsDataMap };

    projectsDataIndex.forEach(projectId => {
      projects[projectId].currentlyActive = {};

      const project = projects[projectId];

      // add full resource data into returned project object
      // lack of a property on the original project object indicates a lack of list/view permissions for that data
      // we need to call build entity map so that any resource pusher updates are applied to the project data originally returned
      ALL_RESOURCE_KEYS.forEach(key => {
        if (project.hasOwnProperty(key)) {
          projects[projectId].currentlyActive[key] =
            projectSelector.buildActiveEntityValue(project[key]);
        }
      });

      if (project.hasOwnProperty("charge") && chargeDataMap) {
        projects[projectId].charge = chargeDataMap[projectId];
      }

      if (project.hasOwnProperty("usage") && usageDataMap) {
        projects[projectId].usage = usageDataMap[projectId];
      }
    });

    return operationSelector.stampOperationPermissions(
      cloneDeep(projects),
      permissions
    );
  }
);

// using the following export pattern so we can mock more easily using jest

const projectSelector = {
  chargeQuery,
  usageQuery,
  projectQuery,
  buildEntityMap,
  buildActiveEntityValue,
  selectProjectsDataMap,
  selectProject,
  selectSlimProject
};

export default projectSelector;
